import CountUp from 'react-countup';
import useTheme from 'chat-ui/src/hooks/useTheme';
import {
  translateCompletedInterviews,
  translateInterviewsInProgress,
  translateCompletionRate,
  translateStatistics,
  translateTotal,
  translateTimeToCompletion,
  translateMedianTime,
  translateAverageTime,
} from '../project_results/resultsPageTranslations';
import { StatisticsProps } from '@/types';
import StatisticCard from './StatisticCard';
import { useCurrentProject } from '@/hooks/useCurrentProject';

function Statistics({ data, isLoading }: StatisticsProps) {
  const { currentTheme } = useTheme();
  const { currentProject: singleProjectData } = useCurrentProject();
  const analysisLanguage = singleProjectData?.analysis_language;

  // Average time formatting
  const avgTotalSeconds = Math.floor((data?.average_time || 0) / 1000);
  const avgHours = Math.floor(avgTotalSeconds / 3600);
  const avgMinutes = Math.floor((avgTotalSeconds % 3600) / 60);
  const avgRemainingSeconds = avgTotalSeconds % 60;
  const formattedAvgHours = avgHours.toString().padStart(2, '0');
  const formattedAvgMinutes = avgMinutes.toString().padStart(2, '0');
  const formattedAvgSeconds = avgRemainingSeconds.toString().padStart(2, '0');

  // Median time formatting
  const medianTotalSeconds = Math.floor((data?.median_time || 0) / 1000);
  const medianHours = Math.floor(medianTotalSeconds / 3600);
  const medianMinutes = Math.floor((medianTotalSeconds % 3600) / 60);
  const medianRemainingSeconds = medianTotalSeconds % 60;
  const formattedMedianHours = medianHours.toString().padStart(2, '0');
  const formattedMedianMinutes = medianMinutes.toString().padStart(2, '0');
  const formattedMedianSeconds = medianRemainingSeconds.toString().padStart(2, '0');

  return (
    <div className="flex flex-col w-full">
      <div className="flex items-start justify-between w-full">
        <h3
          style={{ color: currentTheme?.primary }}
          className="!mb-6 text-lg font-bold epilogue-font"
        >
          {translateStatistics[analysisLanguage] || translateStatistics.English} (
          {translateTotal[analysisLanguage] || translateTotal.English})
        </h3>
      </div>
      <div className="grid grid-cols-4 gap-4 max1100:grid-cols-3 md800:grid-cols-2 min500:grid-cols-1">
        {/* completed interviews */}
        <StatisticCard
          isLoading={isLoading}
          bgColor={currentTheme?.primary}
          title={
            translateCompletedInterviews[analysisLanguage] || translateCompletedInterviews.English
          }
        >
          <CountUp end={data?.completed_interviews} /> / {singleProjectData?.respondents_max || 0}
        </StatisticCard>
        {/* started interviews */}
        <StatisticCard
          isLoading={isLoading}
          title={
            translateInterviewsInProgress[analysisLanguage] || translateInterviewsInProgress.English
          }
        >
          <CountUp end={data?.started_interviews} />
        </StatisticCard>
        {/* completion rate */}
        <StatisticCard
          isLoading={isLoading}
          title={translateCompletionRate[analysisLanguage] || translateCompletionRate.English}
        >
          <CountUp end={data?.completion_rate} /> %
        </StatisticCard>

        {/* time to completion */}
        <StatisticCard
          isLoading={isLoading}
          title={translateTimeToCompletion[analysisLanguage] || translateTimeToCompletion.English}
        >
          <div className="flex flex-col text-xl">
            <div className="mb-1">
              <span className="text-sm font-medium">
                {translateMedianTime[analysisLanguage] || translateMedianTime.English}:
              </span>{' '}
              {+formattedMedianHours > 0 ? <CountUp end={+formattedMedianHours} /> : 0}h :
              {+formattedMedianMinutes ? <CountUp end={+formattedMedianMinutes} /> : 0}m :
              {+formattedMedianSeconds < 10 && '0'}
              <CountUp end={Number(formattedMedianSeconds)} />s
            </div>
            <div>
              <span className="text-sm font-medium">
                {translateAverageTime[analysisLanguage] || translateAverageTime.English}:
              </span>{' '}
              {+formattedAvgHours > 0 ? <CountUp end={+formattedAvgHours} /> : 0}h :
              {+formattedAvgMinutes ? <CountUp end={+formattedAvgMinutes} /> : 0}m :
              {+formattedAvgSeconds < 10 && '0'}
              <CountUp end={Number(formattedAvgSeconds)} />s
            </div>
          </div>
        </StatisticCard>
      </div>
    </div>
  );
}

export default Statistics;
